import { Button, Grid } from "@mui/material";
import Box from "@mui/material/Box";
import * as React from "react";
import { useRecordContext } from "react-admin";
import { Controller, useForm } from "react-hook-form";
import {
  default as CustomProductIntTextField,
  default as CustomProductNumberTextField,
} from "../../unit/forms/CustomProductIntTextField";
import CustomProductSelect from "../../unit/forms/CustomProductSelect";
import FieldTitle from "./FieldTitle";
import ProductLinkButton from "./ProductLinkButton";
import SectionStyled from "./SectionStyled";
import SectionTitle from "./SectionTitle";
import CustomProductActionBottom from "../CustomProductActionBottom";

export default function Fees({ onSave, defaultValues, ...props }: any) {
  const record = useRecordContext();
  const data = record?.data?.basic?.fees;
  const subType = record?.data?.basic?.subType;
  const productModulesGroup = record?.data?.basic?.productModulesGroup;

  // 构建初始值
  const buildInitialValues = () => {
    if (data) {
      return {
        applicationFee: data?.iApplicationFee?.toString() || "",
        applicationFeeUnit: data?.sApplicationFeeUnit || "Amount",
        sChargeTime: data?.sChargeTime || "",
        sChargeMethod: data?.sChargeMethod || "",
        originationFee: data?.iOriginationFee?.toString() || "",
        originationFeeUnit: data?.sOriginationFeeUnit || "Amount",
        sEmploymentCheckUnit: data?.sEmploymentCheckUnit || "Amount",
        sCreditCheckFeeUnit: data?.sCreditCheckFeeUnit || "Amount",
        iEmploymentCheckFee: data?.iEmploymentCheckFee?.toString() || "",
        iCreditCheckFee: data?.iCreditCheckFee?.toString() || "",
        balance_transfer_fee: data?.balance_transfer_fee?.toString() || "",
        balance_transfer_fee_unit: data?.balance_transfer_fee_unit || "Amount",
        cash_advance_fee: data?.cash_advance_fee?.toString() || "",
        cash_advance_fee_unit: data?.cash_advance_fee_unit || "Amount",
        foreign_transaction_fee:
          data?.foreign_transaction_fee?.toString() || "",
        foreign_transaction_fee_unit:
          data?.foreign_transaction_fee_unit || "Amount",
        annual_fee: data?.annual_fee?.toString() || "",
        annual_fee_unit: data?.annual_fee_unit || "Amount",
        returned_payment_fee: data?.returned_payment_fee?.toString() || "",
        returned_payment_fee_unit: data?.returned_payment_fee_unit || "Amount",
        statement_copy_fee: data?.statement_copy_fee?.toString() || "",
        statement_copy_fee_unit: data?.statement_copy_fee_unit || "Amount",
        emergency_card_replacement_fee:
          data?.emergency_card_replacement_fee?.toString() || "",
        emergency_card_replacement_fee_unit:
          data?.emergency_card_replacement_fee_unit || "Amount",
        card_replacement_fee: data?.card_replacement_fee?.toString() || "",
        card_replacement_fee_unit: data?.card_replacement_fee_unit || "Amount",
        card_recovery_fee: data?.card_recovery_fee?.toString() || "",
        card_recovery_fee_unit: data?.card_recovery_fee_unit || "Amount",
        rush_fee: data?.rush_fee?.toString() || "",
        rush_fee_unit: data?.rush_fee_unit || "Amount",
      };
    }
    return {
      applicationFee: "",
      applicationFeeUnit: "Amount",
      sChargeTime: "",
      sChargeMethod: "",
      originationFee: "",
      originationFeeUnit: "Amount",
      sEmploymentCheckUnit: "Amount",
      sCreditCheckFeeUnit: "Amount",
      iEmploymentCheckFee: "",
      iCreditCheckFee: "",
      balance_transfer_fee: "",
      balance_transfer_fee_unit: "Amount",
      cash_advance_fee: "",
      cash_advance_fee_unit: "Amount",
      foreign_transaction_fee: "",
      foreign_transaction_fee_unit: "Amount",
      annual_fee: "",
      annual_fee_unit: "Amount",
      returned_payment_fee: "",
      returned_payment_fee_unit: "Amount",
      statement_copy_fee: "",
      statement_copy_fee_unit: "Amount",
      emergency_card_replacement_fee: "",
      emergency_card_replacement_fee_unit: "Amount",
      card_replacement_fee: "",
      card_replacement_fee_unit: "Amount",
      card_recovery_fee: "",
      card_recovery_fee_unit: "Amount",
      rush_fee: "",
      rush_fee_unit: "Amount",
    };
  };

  const initialValues = buildInitialValues();

  const {
    control,
    formState: { errors, isDirty, dirtyFields },
    reset,
    getValues,
  } = useForm({
    mode: "onBlur",
    defaultValues: initialValues,
  });

  // 使用 ref 跟踪是否已经通知过父组件初始状态
  const hasNotifiedParent = React.useRef(false);

  // 当 data 变化时，更新表单值
  React.useEffect(() => {
    if (data) {
      const newValues = buildInitialValues();
      console.log("Updating Fees with data:", newValues);

      // 重置表单，不触发 dirty 状态
      reset(newValues, {
        keepDirty: false,
        keepValues: false,
        keepDefaultValues: true,
      });

      // 重置通知标记
      hasNotifiedParent.current = false;
    }
  }, [data, reset]);

  // 监听 isDirty 变化，但只在真正变化时通知父组件
  React.useEffect(() => {
    // 跳过初始渲染
    if (!hasNotifiedParent.current) {
      // 第一次渲染时，确保通知父组件 isDirty 为 false
      if (props?.onDirtyChange) {
        props?.onDirtyChange(props?.curMenu, false);
      }
      hasNotifiedParent.current = true;
    } else {
      // 后续变化才真正触发
      console.log("Fees isDirty changed:", isDirty, dirtyFields);
      if (props?.onDirtyChange) {
        props?.onDirtyChange(props?.curMenu, isDirty);
      }
    }
  }, [isDirty, dirtyFields, props?.curMenu]);

  const handleSaveClick = () => {
    const formData = getValues();
    formData.saveAction = "fees";
    console.log("Saving Fees data:", formData);

    if (onSave) {
      onSave(formData, props?.curMenu);
      // 保存后重置，保持干净状态
      reset(formData, {
        keepDirty: false,
        keepValues: false,
        keepDefaultValues: true,
      });
    }
  };

  // 强制通知父组件当前状态（可用于调试）
  React.useEffect(() => {
    console.log("Current Fees isDirty:", isDirty);
  }, [isDirty]);

  return (
    <SectionStyled id="section_fees">
      <SectionTitle title="Fees - Set fees and charges for processing and financing" />
      <Box
        sx={{
          padding: "10px",
          height: "calc(100vh - 255px)",
          overflowY: "auto",
        }}
      >
        <Grid container spacing={2}>
          {/* Header Row */}
          <Grid item xs={12} container spacing={1}>
            <Grid item xs={3}></Grid>
            <Grid item xs={1}>
              Amount
            </Grid>
            <Grid item xs={1}></Grid>
            <Grid item xs={1}>
              Timing
            </Grid>
            <Grid item xs={1}>
              Method
            </Grid>
            <Grid item xs={5}></Grid>
          </Grid>

          {/* Application Fee */}
          {productModulesGroup?.includes("application_fee") && (
            <Grid item xs={12} container spacing={1}>
              <Grid item xs={3}>
                <FieldTitle
                  title={
                    subType != "Employee"
                      ? "Application Fee"
                      : "Application Fee (Maximum)"
                  }
                />
              </Grid>
              <Grid
                item
                xs={1}
                sx={{ "& .MuiTextField-root": { padding: 0, margin: 0 } }}
              >
                <Controller
                  name="applicationFee"
                  control={control}
                  render={({ field }) => (
                    <CustomProductIntTextField {...field} />
                  )}
                />
              </Grid>
              <Grid item xs={1}>
                <Controller
                  name="applicationFeeUnit"
                  control={control}
                  render={({ field }) => (
                    <CustomProductSelect
                      {...field}
                      fullWidth
                      variant="outlined"
                      optionlist={[
                        { value: "Amount", label: "$" },
                        { value: "Percentage", label: "%" },
                      ]}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={1}>
                <Controller
                  name="sChargeTime"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <CustomProductSelect
                      {...field}
                      fullWidth
                      variant="outlined"
                      optionlist={[
                        { value: "", label: "Select" },
                        { value: "Submitted", label: "Submission" },
                        { value: "Approved", label: "Approval" },
                      ]}
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={1}>
                <Controller
                  name="sChargeMethod"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <CustomProductSelect
                      {...field}
                      fullWidth
                      variant="outlined"
                      optionlist={[
                        { value: "", label: "Select" },
                        { value: "Card and ACH", label: "Card and ACH" },
                        { value: "ACH", label: "ACH Only" },
                        { value: "Card", label: "Card Only" },
                      ]}
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={5}></Grid>
            </Grid>
          )}

          {/* Origination Fee */}
          {productModulesGroup?.includes("origination") && (
            <Grid item xs={12} container spacing={1}>
              <Grid item xs={3}>
                <FieldTitle title="Origination Fee" />
              </Grid>
              <Grid item xs={1}>
                <Controller
                  name="originationFee"
                  control={control}
                  render={({ field }) => (
                    <CustomProductNumberTextField
                      {...field}
                      sx={{ marginTop: "-6px" }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={1}>
                <Controller
                  name="originationFeeUnit"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <CustomProductSelect
                      {...field}
                      fullWidth
                      variant="outlined"
                      optionlist={[
                        { value: "Amount", label: "$" },
                        { value: "Percentage", label: "%" },
                      ]}
                      error={!!error}
                      helperText={error?.message}
                      sx={{ marginTop: "-6px" }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={7}></Grid>
            </Grid>
          )}

          {/* Employer Application Fee */}
          {productModulesGroup?.includes("employee_application_fee") && (
            <Grid item xs={12} container spacing={1}>
              <Grid item xs={3}>
                <FieldTitle title="Employer Application Fee" />
              </Grid>
              <Grid item xs={9}>
                <ProductLinkButton
                  title="Differentiate by Employer"
                  href="/employers"
                />
                &nbsp;&nbsp;( Please check it on Employer List )
              </Grid>
            </Grid>
          )}

          {/* Credit Check Fee Per Applicant */}
          {productModulesGroup?.includes("credit") && (
            <Grid item xs={12} container spacing={1}>
              <Grid item xs={3}>
                <FieldTitle title="Credit Check Fee Per Applicant" />
              </Grid>
              <Grid item xs={1}>
                <Controller
                  name="iCreditCheckFee"
                  control={control}
                  render={({ field }) => (
                    <CustomProductNumberTextField {...field} />
                  )}
                />
              </Grid>
              <Grid item xs={1}>
                <Controller
                  name="sCreditCheckFeeUnit"
                  control={control}
                  render={({ field }) => (
                    <CustomProductSelect
                      {...field}
                      fullWidth
                      variant="outlined"
                      optionlist={[{ value: "Amount", label: "$" }]}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={7}></Grid>
            </Grid>
          )}

          {/* Employment Check Fee */}
          {productModulesGroup?.includes("processing_fee") && (
            <Grid item xs={12} container spacing={1}>
              <Grid item xs={3}>
                <FieldTitle title="Employment Check Fee Per Applicant" />
              </Grid>
              <Grid item xs={1}>
                <Controller
                  name="iEmploymentCheckFee"
                  control={control}
                  render={({ field }) => (
                    <CustomProductNumberTextField {...field} />
                  )}
                />
              </Grid>
              <Grid item xs={1}>
                <Controller
                  name="sEmploymentCheckUnit"
                  control={control}
                  render={({ field }) => (
                    <CustomProductSelect
                      {...field}
                      fullWidth
                      variant="outlined"
                      optionlist={[{ value: "Amount", label: "$" }]}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={8}></Grid>
            </Grid>
          )}

          {/* Credit Card Fees */}
          {productModulesGroup?.includes("credit_fee") && (
            <>
              <Grid item xs={12} container spacing={1}>
                <Grid item xs={3}>
                  <FieldTitle title="Balance Transfer Fee" />
                </Grid>
                <Grid item xs={1}>
                  <Controller
                    name="balance_transfer_fee"
                    control={control}
                    render={({ field }) => (
                      <CustomProductNumberTextField {...field} />
                    )}
                  />
                </Grid>
                <Grid item xs={1}>
                  <Controller
                    name="balance_transfer_fee_unit"
                    control={control}
                    render={({ field }) => (
                      <CustomProductSelect
                        {...field}
                        fullWidth
                        variant="outlined"
                        optionlist={[
                          { value: "Amount", label: "$" },
                          { value: "Percentage", label: "%" },
                        ]}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={7}></Grid>
              </Grid>

              <Grid item xs={12} container spacing={1}>
                <Grid item xs={3}>
                  <FieldTitle title="Cash Advance Fee" />
                </Grid>
                <Grid item xs={1}>
                  <Controller
                    name="cash_advance_fee"
                    control={control}
                    render={({ field }) => (
                      <CustomProductNumberTextField {...field} />
                    )}
                  />
                </Grid>
                <Grid item xs={1}>
                  <Controller
                    name="cash_advance_fee_unit"
                    control={control}
                    render={({ field }) => (
                      <CustomProductSelect
                        {...field}
                        fullWidth
                        variant="outlined"
                        optionlist={[
                          { value: "Amount", label: "$" },
                          { value: "Percentage", label: "%" },
                        ]}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={7}></Grid>
              </Grid>

              <Grid item xs={12} container spacing={1}>
                <Grid item xs={3}>
                  <FieldTitle title="Foreign Transaction Fee" />
                </Grid>
                <Grid item xs={1}>
                  <Controller
                    name="foreign_transaction_fee"
                    control={control}
                    render={({ field }) => (
                      <CustomProductNumberTextField {...field} />
                    )}
                  />
                </Grid>
                <Grid item xs={1}>
                  <Controller
                    name="foreign_transaction_fee_unit"
                    control={control}
                    render={({ field }) => (
                      <CustomProductSelect
                        {...field}
                        fullWidth
                        variant="outlined"
                        optionlist={[
                          { value: "Amount", label: "$" },
                          { value: "Percentage", label: "%" },
                        ]}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={7}></Grid>
              </Grid>

              <Grid item xs={12} container spacing={1}>
                <Grid item xs={3}>
                  <FieldTitle title="Annual Fee" />
                </Grid>
                <Grid item xs={1}>
                  <Controller
                    name="annual_fee"
                    control={control}
                    render={({ field }) => (
                      <CustomProductNumberTextField {...field} />
                    )}
                  />
                </Grid>
                <Grid item xs={1}>
                  <Controller
                    name="annual_fee_unit"
                    control={control}
                    render={({ field }) => (
                      <CustomProductSelect
                        {...field}
                        fullWidth
                        variant="outlined"
                        optionlist={[
                          { value: "Amount", label: "$" },
                          { value: "Percentage", label: "%" },
                        ]}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={7}></Grid>
              </Grid>

              <Grid item xs={12} container spacing={1}>
                <Grid item xs={3}>
                  <FieldTitle title="Returned Payment Fee" />
                </Grid>
                <Grid item xs={1}>
                  <Controller
                    name="returned_payment_fee"
                    control={control}
                    render={({ field }) => (
                      <CustomProductNumberTextField {...field} />
                    )}
                  />
                </Grid>
                <Grid item xs={1}>
                  <Controller
                    name="returned_payment_fee_unit"
                    control={control}
                    render={({ field }) => (
                      <CustomProductSelect
                        {...field}
                        fullWidth
                        variant="outlined"
                        optionlist={[
                          { value: "Amount", label: "$" },
                          { value: "Percentage", label: "%" },
                        ]}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={7}></Grid>
              </Grid>

              <Grid item xs={12} container spacing={1}>
                <Grid item xs={3}>
                  <FieldTitle title="Statement Copy Fee" />
                </Grid>
                <Grid item xs={1}>
                  <Controller
                    name="statement_copy_fee"
                    control={control}
                    render={({ field }) => (
                      <CustomProductNumberTextField {...field} />
                    )}
                  />
                </Grid>
                <Grid item xs={1}>
                  <Controller
                    name="statement_copy_fee_unit"
                    control={control}
                    render={({ field }) => (
                      <CustomProductSelect
                        {...field}
                        fullWidth
                        variant="outlined"
                        optionlist={[
                          { value: "Amount", label: "$" },
                          { value: "Percentage", label: "%" },
                        ]}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={7}></Grid>
              </Grid>

              <Grid item xs={12} container spacing={1}>
                <Grid item xs={3}>
                  <FieldTitle title="Emergency Card Replacement Fee" />
                </Grid>
                <Grid item xs={1}>
                  <Controller
                    name="emergency_card_replacement_fee"
                    control={control}
                    render={({ field }) => (
                      <CustomProductNumberTextField {...field} />
                    )}
                  />
                </Grid>
                <Grid item xs={1}>
                  <Controller
                    name="emergency_card_replacement_fee_unit"
                    control={control}
                    render={({ field }) => (
                      <CustomProductSelect
                        {...field}
                        fullWidth
                        variant="outlined"
                        optionlist={[
                          { value: "Amount", label: "$" },
                          { value: "Percentage", label: "%" },
                        ]}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={7}></Grid>
              </Grid>

              <Grid item xs={12} container spacing={1}>
                <Grid item xs={3}>
                  <FieldTitle title="Card Replacement Fee" />
                </Grid>
                <Grid item xs={1}>
                  <Controller
                    name="card_replacement_fee"
                    control={control}
                    render={({ field }) => (
                      <CustomProductNumberTextField {...field} />
                    )}
                  />
                </Grid>
                <Grid item xs={1}>
                  <Controller
                    name="card_replacement_fee_unit"
                    control={control}
                    render={({ field }) => (
                      <CustomProductSelect
                        {...field}
                        fullWidth
                        variant="outlined"
                        optionlist={[
                          { value: "Amount", label: "$" },
                          { value: "Percentage", label: "%" },
                        ]}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={7}></Grid>
              </Grid>

              <Grid item xs={12} container spacing={1}>
                <Grid item xs={3}>
                  <FieldTitle title="Card Recovery Fee" />
                </Grid>
                <Grid item xs={1}>
                  <Controller
                    name="card_recovery_fee"
                    control={control}
                    render={({ field }) => (
                      <CustomProductNumberTextField {...field} />
                    )}
                  />
                </Grid>
                <Grid item xs={1}>
                  <Controller
                    name="card_recovery_fee_unit"
                    control={control}
                    render={({ field }) => (
                      <CustomProductSelect
                        {...field}
                        fullWidth
                        variant="outlined"
                        optionlist={[
                          { value: "Amount", label: "$" },
                          { value: "Percentage", label: "%" },
                        ]}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={7}></Grid>
              </Grid>

              <Grid item xs={12} container spacing={1}>
                <Grid item xs={3}>
                  <FieldTitle title="Rush Fee" />
                </Grid>
                <Grid item xs={1}>
                  <Controller
                    name="rush_fee"
                    control={control}
                    render={({ field }) => (
                      <CustomProductNumberTextField {...field} />
                    )}
                  />
                </Grid>
                <Grid item xs={1}>
                  <Controller
                    name="rush_fee_unit"
                    control={control}
                    render={({ field }) => (
                      <CustomProductSelect
                        {...field}
                        fullWidth
                        variant="outlined"
                        optionlist={[
                          { value: "Amount", label: "$" },
                          { value: "Percentage", label: "%" },
                        ]}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={7}></Grid>
              </Grid>
            </>
          )}
        </Grid>
      </Box>

      {/* Save Button */}
      <CustomProductActionBottom>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSaveClick}
          disabled={!isDirty}
          sx={{ width: "120px", mt: 1 }}
        >
          Save
        </Button>
      </CustomProductActionBottom>
    </SectionStyled>
  );
}
